import {
  NetworkStatus,
  useApolloClient,
  useMutation,
  useQuery,
} from '@apollo/client';
import { Icon, IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import {
  showCustomToast,
} from 'common/components/CustomToast/utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { TABLE_ROWS } from 'common/constants';
import {
  EntityDeleteInput,
  EntityDocumentFilter,
} from 'common/types/globalTypes';
import clsx from 'clsx';
import { AmountTextView } from 'common/components/AmountView/AmountTextView';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { DocumentViewModal } from 'common/components/DocumentList/DocumentViewModal';
import { DownloadButton } from 'common/components/DownloadButton';
import { InfiniteList } from 'common/components/InfiniteList';
import { ColumnData } from 'common/components/SearchBar';
import { CompanyCurrencies } from 'common/graphql/__generated__/CompanyCurrencies';
import {
  EntityDocumentDelete,
  EntityDocumentDeleteVariables,
} from 'common/graphql/__generated__/EntityDocumentDelete';
import { formatDropdownOptions } from 'common/utils';
import { getSortedColumns } from 'common/utils/columnUtilities';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import {
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import { fileType, fileTypeColor } from 'common/utils/fileType';
import {
  GetDocumentPoolCommonData,
  GetDocumentPoolCommonDataVariables,
} from 'documents/__generated__/GetDocumentPoolCommonData';
import { ExtractionTypes } from 'documents/documentAssignment/folder/list/FolderView/FolderViewHeader/UploadForm/__generated__/ExtractionTypes';
import { filterOptionProps } from 'documents/documentAssignment/folder/list/GroupHeader';
import {
  PersoanlDocuments,
  PersoanlDocuments_entityDocumentPersonalPools_nodes,
  PersoanlDocumentsVariables,
} from './__generated__/PersonalDocuments';
import { documentColumns } from './columns.data';
import { useStyles } from './index.styles';
import {
  toFilterOrVariable,
  toFilterVariable,
  toOrderByVariable,
} from './utils';

import CURRENCY_DATA from 'common/graphql/CompanyCurrencies.graphql';
import GENERAL_DATA from '../../../documents/GetDocumentPoolCommonData.graphql';
import DOCUMENTSLIST from './PersonalDocuments.graphql';
import DELETE_DOCUMENTS from 'common/graphql/EntityDocumentDelete.graphql';
import EXTRACTION_TYPES from '../../documentAssignment/folder/list/FolderView/FolderViewHeader/UploadForm/ExtractionTypes.graphql';
import { MyDocumentsView } from '../MyDocumentsView';
import { UploadDocumentModal } from '../Upload/UploadDocumentModal';
import { HighLightActiveLink } from 'common/components/HighLight';
import { EntityDocumentsPersonalPool_entityDocuments_nodes } from '../__generated__/EntityDocumentsPersonalPool';

type DocumentListItemType = EntityDocumentsPersonalPool_entityDocuments_nodes;
type DocumentProps = PersoanlDocuments_entityDocumentPersonalPools_nodes;

interface MyDocumentListProps {
  selectedItems: (dataList: DocumentProps[]) => void;
  openDeleteDialog: boolean;
  onDismiss: () => void;
  toggleHideUploadDialog: () => void;
  uploadFormVisibility: boolean;
  filterOptions: filterOptionProps;
  callDeleteFunction?: boolean;
  resetFunctionCall: (value: boolean) => void;
}

export const MyDocumentList: React.FC<MyDocumentListProps> = ({
  selectedItems,
  onDismiss,
  toggleHideUploadDialog,
  uploadFormVisibility,
  callDeleteFunction,
  resetFunctionCall,
  filterOptions,
}) => {
  const styles = useStyles();

  const [hideUpdateDialog, { setTrue: showUpdateDialog, setFalse: closeUpdateDialog }] = useBoolean(false);

  const [selectedDocument, setSelectedDocument] =
    useState<DocumentListItemType | null>(null);
  const [defaultDocumentTypeId, setDefaultDocumentTypeId] = useState<number>();
  const [gridColumns, setGridColumns] = useState<ColumnData[]>(documentColumns);
  const [selectedList, setselectedList] = useState<DocumentProps[]>([]);
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>({
    column: 'date',
    direction: 'desc' as OrderDirection,
  });
  const [docViewState, setDocViewState] = useState<DocumentViewModalState>({
    isOpen: false,
    _fileType: 'pdf',
  });
  const client = useApolloClient();
  const { data: GeneralData } = useQuery<
    GetDocumentPoolCommonData,
    GetDocumentPoolCommonDataVariables
  >(GENERAL_DATA, {
    variables: {
      isDocumentUpload: true,
    },
  });
  const { data: extractionTypesData } = useQuery<ExtractionTypes>(
    EXTRACTION_TYPES,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );
  const { data: currencyData } = useQuery<CompanyCurrencies>(CURRENCY_DATA);
  const {
    loading: personalDocumentLoading,
    data: personalDocumentListData,
    error: personalDocumentListDataError,
    fetchMore,
    refetch,
    variables,
    networkStatus,
  } = useQuery<PersoanlDocuments, PersoanlDocumentsVariables>(DOCUMENTSLIST, {
    variables: {
      first: TABLE_ROWS,
      orderBy: ['_UPLOAD_DATE_DESC', 'PRIMARY_KEY_ASC'],
      filter: {
        _isReportingDocument: { equalTo: false },
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });


  const documentOptions =
    GeneralData?.personalPoolAvailableDocumentTypes?.nodes.map((item) => ({
      key: item.id,
      text: item?.documentType! || '',
      isAccountingDocument: item?.isAccountingDocument || undefined,
      isSigningRequired: item?.isSigningRequired,
      extractionTypes: item.extractionTypes,
    }));

  const refetching =
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables ||
    networkStatus === NetworkStatus.loading;

  const onColumnClick = (clickedColumn: ColumnData) => {
    const { newColumns, desc } = getSortedColumns(clickedColumn, gridColumns);
    setGridColumns(newColumns);
    setSortOrderParam({
      column: clickedColumn.key,
      direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
    });
  };

  const _renderItemColumn = (
    item: DocumentListItemType | undefined,
    _index: number | undefined,
    column: IColumn | undefined
  ) => {
    const fieldContent = item?.[
      column?.fieldName as keyof DocumentListItemType
    ] as string;
    if (item) {
      switch (column?.key) {
        case 'documentType':
          return (
            <HighLightActiveLink
              preventDefault
              highlightText={''}
              text={item.documentTypes?.documentType ?? ''}
              onLinkClick={() => {
                setDefaultDocumentTypeId(item.documentTypes?.id);
                setSelectedDocument(item);
                showUpdateDialog();

              }}
            />
          );
        case '_documentContents':
          return (
            <Stack
              horizontal
              verticalAlign="center"
              className={styles.columnStack}
            >
              {item._documentContents && (
                <Text variant="medium">{item._documentContents}</Text>
              )}
            </Stack>
          );
        case 'isoCode':
          return (
            <Stack
              horizontal
              verticalAlign="center"
              className={styles.columnStack}
            >
              {item.currency?.isoCode && (
                <Text variant="medium">{item.currency?.isoCode}</Text>
              )}
            </Stack>
          );
        case 'indexAmount':
          return (
            <Stack
              horizontal
              verticalAlign="center"
              horizontalAlign="end"
              className={styles.columnStack}
            >
              <AmountTextView value={item.indexAmount} />
            </Stack>
          );
        case 'usedTotal':
          return (
            <Stack
              horizontal
              verticalAlign="center"
              horizontalAlign="end"
              className={styles.columnStack}
            >
              <AmountTextView value={item.documentAppliedAmounts?.usedTotal!} />
            </Stack>
          );
        case 'remainingTotal':
          return (
            <Stack
              horizontal
              verticalAlign="center"
              horizontalAlign="end"
              className={styles.columnStack}
            >
              <AmountTextView
                value={item.documentAppliedAmounts?.remainingTotal!}
              />
            </Stack>
          );

        case 'comment':
          return (
            <Stack
              horizontal
              verticalAlign="center"
              className={styles.columnStack}
            >
              {item.comment && <Text variant="medium">{item.comment}</Text>}
            </Stack>
          );
        case 'fileReference':
          return (
            <Stack
              horizontal
              verticalAlign="center"
              className={clsx(styles.columnStack, styles.fileReferenceCell)}
            >
              <Icon
                iconName={fileType(item.iconType || 'OTHER')}
                className={fileTypeColor(item.iconType || 'OTHER')}
              />
              <Text variant="medium">{item.fileReference}</Text>
            </Stack>
          );
        case '_sharedByUser':
          return (
            <Stack verticalAlign="center" className={styles.columnStack}>
              {item._sharedByUser && (
                <Text variant="medium">{item._sharedByUser}</Text>
              )}
            </Stack>
          );
        case 'action':
          return (
            <Stack verticalAlign="center" tokens={{ childrenGap: 3 }}>
              <DownloadButton entityDocumentId={item.id} />
            </Stack>
          );
        case 'view':
          const viewDocumentDisable =
            (item._isProtected !== undefined || item._fileViewer !== undefined) &&
            (item._isProtected || item._fileViewer !== 'browser');
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentDisable}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    setDocViewState({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );

        case '_uploadDate':
          return (
            <Stack
              horizontal
              verticalAlign="center"
              horizontalAlign="end"
              className={styles.columnStack}
            >
              <Text variant="medium">
                {getGlobalDateFormat(item._uploadDate || '')}
              </Text>
            </Stack>
          );
        default:
          return (
            <Stack
              horizontal
              verticalAlign="center"
              className={styles.columnStack}
            >
              <Text>{fieldContent}</Text>
            </Stack>
          );
      }
    }
  };

  const [deleteDocuments] = useMutation<
    EntityDocumentDelete,
    EntityDocumentDeleteVariables
  >(DELETE_DOCUMENTS, { errorPolicy: 'all' });

  const currencyTypes = formatDropdownOptions(
    currencyData?.companyCurrencies?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.isoCode + '-' + item.name!,
      includeAll: false,
    }
  );
  const handleDelete = async () => {
    const selectionArray: DocumentProps[] = selectedList;
    const selectedData = selectionArray;
    const toDelete: DocumentProps[] = selectedData.map((_, index) => {
      return selectedData![index];
    });
    let deletableDocs = toDelete?.filter((item) => item._isDeletable !== false);
    if (deletableDocs.length > 0) {
      const deleteInputArray: EntityDeleteInput[] = deletableDocs.map(
        (item) => {
          return { id: item.id, rowTimestamp: item._rowTimestamp! };
        }
      );
      const { errors } = await deleteDocuments({
        variables: { input: { entityDelete: deleteInputArray } },
        optimisticResponse: {
          entityDocumentDelete: {
            deletedEntities: deleteInputArray,
          },
        },
        update: (cache, { data }) => {
          const deletedIds = data?.entityDocumentDelete?.deletedEntities?.map(
            (entity) => entity?.id
          );
          if (deletedIds) {
            const filteredList =
              personalDocumentListData?.entityDocumentPersonalPools?.nodes.filter(
                (emp) => deletedIds.indexOf(emp.id) === -1
              );
            const newData: PersoanlDocuments = {
              entityDocumentPersonalPools: {
                nodes: filteredList!,
                pageInfo:
                  personalDocumentListData?.entityDocumentPersonalPools
                    ?.pageInfo!,
                totalCount:
                  personalDocumentListData?.entityDocumentPersonalPools
                    ?.totalCount! - deletedIds.length,
              },
            };
            cache.writeQuery<PersoanlDocuments, PersoanlDocumentsVariables>({
              query: DOCUMENTSLIST,
              variables,
              data: newData,
            });
          }
        },
      });

      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        resetFunctionCall(false);
        showCustomToast({
          content: 'Record deleted Successfully',
          appearance: 'success',
        });
        onDismiss();
      }
    }
  };

  const handleDeleteMemo = useCallback(handleDelete, [callDeleteFunction]);

  useEffect(() => {
    if (callDeleteFunction) {
      handleDeleteMemo();
    }
  }, [callDeleteFunction, handleDeleteMemo]);

  const { filterTypes, startsWith } = { ...filterOptions };

  const handleSearch = async (showMore?: boolean) => {
    const searchOption = startsWith
      ? 'startsWithInsensitive'
      : 'includesInsensitive';

    const filtersApplied: EntityDocumentFilter | undefined = filterTypes?.length
      ? ({
        and: toFilterVariable(filterTypes, searchOption),
        or: toFilterOrVariable(filterTypes),
      } as EntityDocumentFilter)
      : undefined;
    const variables: PersoanlDocumentsVariables = {
      first: TABLE_ROWS,
      orderBy: toOrderByVariable(sortOrderParam),
      filter: {
        ...filtersApplied,
        _isReportingDocument: { equalTo: false },
      },
      after: showMore
        ? personalDocumentListData?.entityDocumentPersonalPools?.pageInfo
          ?.endCursor
        : undefined,
    };
    if (showMore) fetchMore({ variables });
    else refetch(variables);
  };

  const handleSearchMemo = useCallback(handleSearch, [
    filterTypes,
    sortOrderParam,
    startsWith,
  ]);

  const renderRef = useRef(false);

  useEffect(() => {
    if (renderRef.current) handleSearchMemo(false);
  }, [filterTypes, sortOrderParam, handleSearchMemo]);

  useEffect(() => {
    if (!renderRef.current) renderRef.current = true;
    else {
      handleSearchMemo(false);
    }
  }, [startsWith, handleSearchMemo]);

  return (
    <>
      <InfiniteList
        loading={
          (!personalDocumentListData && !personalDocumentListDataError) ||
          personalDocumentLoading
        }
        items={
          !refetching
            ? personalDocumentListData?.entityDocumentPersonalPools?.nodes || []
            : undefined
        }
        hasNextPage={
          personalDocumentListData?.entityDocumentPersonalPools?.pageInfo
            .hasNextPage
        }
        columns={gridColumns.filter((_column) => _column.isVisible)}
        onRenderItemColumn={_renderItemColumn}
        onColumnHeaderClick={(_, column) => {
          // if (column) _onColumnClick(column);
          if (column) onColumnClick(column);
        }}
        // onLoadMore={() => handleSortAndPagination(sortOrderParam, true)}
        onLoadMore={async () => await handleSearch(true)}
        onSelectionChanged={(arr) => {
          setselectedList(arr);
          selectedItems(arr);
        }}
        isSelectedOnFocus={false}
      />
      <Stack style={{ alignSelf: 'center' }}>
        <DocumentViewModal
          centerAlign
          onDismiss={() => setDocViewState({ isOpen: false, _fileType: 'pdf' })}
          {...docViewState}
        />
      </Stack>

      {hideUpdateDialog && selectedDocument && (
        <MyDocumentsView
          visible={hideUpdateDialog}
          onDismiss={closeUpdateDialog}
          documentOptions={documentOptions!}
          companyCurrencies={currencyTypes}
          documentData={selectedDocument}
          defaultDocumentTypeId={defaultDocumentTypeId}
          onUpdate={(updatedDocuments) => {
            const position = selectedList.findIndex((item) => updatedDocuments[0].id === item.id);
            let newSelectedList = [...selectedList];
            newSelectedList[position] = updatedDocuments[0];
            setselectedList(newSelectedList);
            selectedItems(newSelectedList);
          }}
        />
      )}
      {uploadFormVisibility && (
        <UploadDocumentModal
          visible={uploadFormVisibility}
          onDismiss={toggleHideUploadDialog}
          documentOptions={documentOptions!}
          companyCurrencies={currencyTypes}
          extractionTypesData={extractionTypesData}
        />
      )}

    </>
  );
};
